(() => {
  const SELECTOR = '[data-mj-designcase-index]';
  const MODEL_RE = /\b(?:GL|GP|GU|EL|EDF|EHL|EPL)\d{3,4}\b/i;

  const normalize = (value) => String(value || '').trim();
  const lower = (value) => normalize(value).toLocaleLowerCase();
  const unique = (items) => [...new Set(items.filter(Boolean))];
  const MODEL_ALIASES = {
    EHL107: ['EHL107', 'EL2974'],
    EL2974: ['EHL107', 'EL2974']
  };
  const canonicalModel = (code) => (MODEL_ALIASES[code] || [code])[0];
  const modelCodesFor = (code) => MODEL_ALIASES[code] || [code];

  class DesigncaseIndex {
    constructor(root) {
      this.root = root;
      this.dataUrl = root.dataset.dataUrl;
      this.blogUrl = root.dataset.blogUrl || window.location.pathname;
      this.data = null;
      this.state = {
        color: '',
        model: '',
        q: ''
      };

      this.filterPanel = root.querySelector('[data-filter-panel]');
      this.modelFilterGroup = root.querySelector('[data-model-filter-group]');
      this.colorChips = root.querySelector('[data-color-chips]');
      this.modelChips = root.querySelector('[data-model-chips]');
      this.searchForm = root.querySelector('[data-search-form]');
      this.searchInput = root.querySelector('[data-search-input]');
      this.grid = root.querySelector('[data-grid]');
      this.staticLinks = root.querySelector('[data-static-links]');
      this.loading = root.querySelector('[data-loading]');
      this.empty = root.querySelector('[data-empty]');
      this.status = root.querySelector('[data-status]');
      this.statusText = root.querySelector('[data-status-text]');
      this.copyLink = root.querySelector('[data-copy-link]');
      this.clearFilters = root.querySelector('[data-clear-filters]');
      this.filtersExpanded = true;
      this.filterToggle = null;
    }

    async init() {
      this.readStateFromUrl();
      await this.loadData();
      this.bindEvents();
      this.setupFilterToggle();
      this.renderFilters();
      this.applyState({ replace: true });
      this.setFiltersExpanded(!this.hasActiveFilters());
      this.loading.hidden = true;
      this.grid.hidden = false;
      this.hideStaticFallback();
    }

    async loadData() {
      const response = await fetch(this.dataUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Could not load designcase data');
      this.data = await response.json();
    }

    readStateFromUrl() {
      const url = new URL(window.location.href);
      this.state.color = normalize(url.searchParams.get('color'));
      this.state.model = canonicalModel(normalize(url.searchParams.get('model')).toUpperCase());
      this.state.q = normalize(url.searchParams.get('q'));

      const taggedMatch = decodeURIComponent(url.pathname).match(/\/tagged\/([^/]+)$/);
      if (!this.state.model && taggedMatch) {
        const tag = normalize(taggedMatch[1]);
        if (MODEL_RE.test(tag)) this.state.model = canonicalModel(tag.toUpperCase());
        else this.state.color = tag;
      }
    }

    bindEvents() {
      this.searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.state.q = normalize(this.searchInput.value);
        this.applyState();
      });

      this.searchInput.addEventListener('input', () => {
        this.state.q = normalize(this.searchInput.value);
        this.applyState();
      });

      this.root.addEventListener('click', (event) => {
        const link = event.target.closest('.mj-designcase-index__card a[href]');
        if (link) this.rememberReturnUrl();
      });

      this.copyLink.addEventListener('click', async () => {
        const href = window.location.href;
        try {
          await navigator.clipboard.writeText(href);
          this.copyLink.textContent = '已複製';
          window.setTimeout(() => { this.copyLink.textContent = '複製連結'; }, 1400);
        } catch {
          window.prompt('複製目前篩選連結', href);
        }
      });

      this.clearFilters.addEventListener('click', () => {
        this.state.color = '';
        this.state.model = '';
        this.state.q = '';
        this.searchInput.value = '';
        this.applyState();
        this.setFiltersExpanded(true);
      });

      window.addEventListener('popstate', () => {
        this.readStateFromUrl();
        this.applyState({ replace: true });
        this.setFiltersExpanded(!this.hasActiveFilters());
      });
    }

    hasActiveFilters() {
      return Boolean(this.state.color || this.state.model || this.state.q);
    }

    setupFilterToggle() {
      const actions = this.status && this.status.querySelector('.mj-designcase-index__status-actions');
      if (!actions || this.filterToggle) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mj-designcase-index__status-action';
      button.setAttribute('data-filter-toggle', '');
      button.addEventListener('click', () => this.setFiltersExpanded(!this.filtersExpanded));
      actions.insertBefore(button, this.clearFilters);
      this.filterToggle = button;
    }

    setFiltersExpanded(expanded) {
      this.filtersExpanded = Boolean(expanded);
      this.filterPanel.hidden = false;
      this.modelFilterGroup.hidden = !this.filtersExpanded;
      if (!this.filterToggle) return;

      this.filterToggle.textContent = this.filtersExpanded ? '收合花色型號' : '展開花色型號';
      this.filterToggle.setAttribute('aria-expanded', String(this.filtersExpanded));
    }

    rememberReturnUrl() {
      try {
        sessionStorage.setItem('mjDesigncaseReturn', JSON.stringify({
          url: window.location.href,
          ts: Date.now()
        }));
      } catch {}
    }

    renderFilters() {
      this.clearInvalidModelForColor();
      this.renderColorChips();
      this.renderModelChips();
      this.searchInput.value = this.state.q;
    }

    renderColorChips() {
      const colors = this.data.colors || [];
      const chips = [
        this.buildChip({ label: '全色系一覽', active: !this.state.color, onClick: () => {
          this.state.color = '';
          this.state.model = '';
          this.applyState();
        } }),
        ...colors.map((color) => this.buildChip({ label: color.name, active: this.state.color === color.name, onClick: () => {
          this.state.color = color.name;
          this.state.model = '';
          this.applyState();
        } }))
      ];
      this.colorChips.replaceChildren(...chips);
    }

    renderModelChips() {
      const shouldShowModels = Boolean(this.state.color || this.state.model);
      this.modelFilterGroup.hidden = !shouldShowModels;
      if (!shouldShowModels) {
        this.modelChips.replaceChildren();
        return;
      }
      const models = this.modelsForCurrentColor();
      const allLabel = '全部花色';
      const chips = [
        this.buildChip({ label: allLabel, active: !this.state.model, model: true, onClick: () => {
          this.state.model = '';
          this.applyState();
        } }),
        ...models.map((model) => this.buildChip({ label: this.modelLabel(model), active: this.state.model === canonicalModel(model.code), model: true, onClick: () => {
          this.state.model = canonicalModel(model.code);
          this.applyState();
        } }))
      ];
      this.modelChips.replaceChildren(...chips);
    }

    modelLabel(model) {
      return model.code === 'EHL107' ? 'EHL107(EL2974)' : model.code;
    }

    buildChip({ label, active, model = false, onClick }) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = model ? 'mj-designcase-index__chip mj-designcase-index__chip--model' : 'mj-designcase-index__chip';
      button.textContent = label;
      button.setAttribute('aria-pressed', String(active));
      button.addEventListener('click', onClick);
      return button;
    }

    modelColor(code) {
      const modelColors = {
        "GL6116": "質感灰色系",
        "GL6117": "栗子棕色系",
        "GU1118": "栗子棕色系",
        "GL6105": "清爽白色系",
        "GP7101": "清爽白色系",
        "GU1114": "清爽白色系",
        "GU1124": "清爽白色系",
        "GU2107": "清爽白色系",
        "GP7109": "清爽白色系",
        "EHL129": "清爽白色系",
        "EHL133": "清爽白色系",
        "GL6101": "淺木紋米白",
        "GL6103": "淺木紋米白",
        "GP7102": "淺木紋米白",
        "GU1116": "淺木紋米白",
        "EHL014": "淺木紋米白",
        "EHL141": "淺木紋米白",
        "GL6104": "木質暖色系",
        "GP7105": "木質暖色系",
        "GU1107": "木質暖色系",
        "GU2111": "木質暖色系",
        "EHL107": "木質暖色系",
        "EL2076": "木質暖色系",
        "GU2106": "淺橡燕麥色",
        "GL6111": "栗子棕色系",
        "EL2077": "栗子棕色系",
        "EHL045": "栗子棕色系",
        "EL2974": "木質暖色系"
};
      const category = /^GU31\d+/i.test(code) ? '板岩' : /^GU3201$/i.test(code) ? '花磚' : /^GU[45]\d+/i.test(code) ? '魚骨人字拼' : modelColors[code.toUpperCase()];
      return category || '';
    }

    isAllowedModelForColor(code) {
      if (this.state.color === '質感灰色系') return code.toUpperCase() === 'GL6116';
      const category = this.modelColor(code);
      if (category) return category === this.state.color;
      if (this.state.color === '板岩') return /^GU31\d{2}$/i.test(code);
      if (this.state.color === '花磚') return /^GU32\d{2}$/i.test(code);
      return !/^GU3\d{3,4}$/i.test(code);
    }

    clearInvalidModelForColor() {
      if (!this.state.color || !this.state.model) return;
      const allowedModels = this.modelsForCurrentColor().map((model) => model.code);
      if (!allowedModels.includes(this.state.model)) this.state.model = '';
    }

    modelsForCurrentColor() {
      const modelMap = new Map((this.data.models || []).map((model) => [model.code, model]));
      if (!this.state.color) return [...modelMap.values()].sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true, sensitivity: 'base' }));
      const color = (this.data.colors || []).find((item) => item.name === this.state.color);
      if (!color) return [];
      return (color.models || [])
        .filter((code) => this.isAllowedModelForColor(code))
        .map((code) => modelMap.get(code))
        .filter(Boolean)
        .sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true, sensitivity: 'base' }));
    }

    applyState({ replace = false } = {}) {
      if (!this.data) return;
      this.renderColorChips();
      this.renderModelChips();
      this.searchInput.value = this.state.q;
      this.renderCases();
      this.renderStatus();
      this.updateUrl({ replace });
    }

    caseMatchesModel(item, code) {
      const models = item.models || [];
      return modelCodesFor(code).some((alias) => models.includes(alias));
    }

    filteredCases() {
      const q = lower(this.state.q);
      return (this.data.cases || []).filter((item) => {
        if (this.state.color && !(item.colorGroups || []).includes(this.state.color)) return false;
        if (this.state.model && !this.caseMatchesModel(item, this.state.model)) return false;
        if (q && !lower(item.searchText).includes(q)) return false;
        return true;
      });
    }

    renderCases() {
      const items = this.filteredCases();
      this.grid.replaceChildren(...items.map((item, index) => this.renderCard(item, index)));
      this.empty.hidden = items.length > 0;
      this.hideStaticFallback();
    }

    hideStaticFallback() {
      if (!this.staticLinks || !this.grid || this.grid.children.length === 0) return;
      this.staticLinks.hidden = true;
      this.staticLinks.setAttribute('aria-hidden', 'true');
      this.staticLinks.style.display = 'none';
    }

    renderCard(item, index) {
      const article = document.createElement('article');
      article.className = 'mj-designcase-index__card';

      const imageLink = document.createElement('a');
      imageLink.className = 'mj-designcase-index__card-image-link';
      imageLink.href = item.url;
      imageLink.setAttribute('aria-label', item.displayTitle || item.title);

      if (item.image) {
        const img = document.createElement('img');
        img.className = 'mj-designcase-index__card-image';
        img.src = this.resizeShopifyImage(item.image, 700);
        img.alt = item.imageAlt || item.displayTitle || item.title;
        img.loading = index < 3 ? 'eager' : 'lazy';
        img.decoding = 'async';
        imageLink.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'mj-designcase-index__card-image';
        imageLink.appendChild(placeholder);
      }

      const content = document.createElement('div');
      content.className = 'mj-designcase-index__card-content';

      const title = document.createElement('h3');
      title.className = 'mj-designcase-index__card-title';
      const titleLink = document.createElement('a');
      titleLink.href = item.url;
      titleLink.textContent = item.displayTitle || item.title;
      title.appendChild(titleLink);

      const tags = document.createElement('div');
      tags.className = 'mj-designcase-index__card-tags';
      this.cardTags(item).forEach((tagText) => {
        const tag = document.createElement('span');
        tag.className = 'mj-designcase-index__card-tag';
        tag.textContent = `#${tagText}`;
        tags.appendChild(tag);
      });

      const readMore = document.createElement('a');
      readMore.className = 'mj-designcase-index__card-link';
      readMore.href = item.url;
      readMore.innerHTML = '看更多 <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"></path></svg>';

      content.append(title, tags, readMore);
      article.append(imageLink, content);
      return article;
    }

    resizeShopifyImage(src, width) {
      if (!src || !src.includes('/cdn/shop/')) return src;
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch {
        return src;
      }
    }

    cardTags(item) {
      const titleModels = String(item.title || item.displayTitle || '').toUpperCase()
        .match(/\b(?:GL|GP|GU|EL|EDF|EHL|EPL)\d{3,4}\b/g) || [];
      const codes = unique(titleModels.length ? titleModels : item.models || []);
      const correctedNames = { GL6116: '雷克雅灰橡', GL6117: '博爾貢棕橡' };
      const ordered = codes.flatMap((code) => {
        const aliases = modelCodesFor(code);
        const product = (item.products || []).find((entry) => aliases.includes(entry.code));
        const model = (this.data.models || []).find((entry) => aliases.includes(entry.code));
        const name = correctedNames[code] || (product && product.name)
          || (item.productNames || {})[code] || (model && model.name);
        const category = this.modelColor(code) || (product && (product.colorGroups || [])[0])
          || (codes.length === 1 && (item.colorGroups || [])[0]);
        return [category, name];
      });
      if (codes.length) return unique(ordered);
      return unique([...(item.colorGroups || []), ...(item.tags || []).filter((tag) => !MODEL_RE.test(tag))]);
    }

    renderStatus() {
      const parts = [];
      if (this.state.color) parts.push(this.state.color);
      if (this.state.model) {
        const model = (this.data.models || []).find((item) => item.code === this.state.model);
        parts.push(model ? this.modelLabel(model) : this.state.model);
      }
      if (this.state.q) parts.push(`搜尋「${this.state.q}」`);

      const count = this.filteredCases().length;
      if (!parts.length) {
        this.status.hidden = true;
        return;
      }
      this.statusText.textContent = `${parts.join(' / ')}，共 ${count} 個案例`;
      this.status.hidden = false;
    }

    updateUrl({ replace = false } = {}) {
      const url = new URL(window.location.href);
      const view = url.searchParams.get('view');
      const previewThemeId = url.searchParams.get('preview_theme_id');
      const taggedMatch = decodeURIComponent(url.pathname).match(/\/tagged\/([^/]+)$/);
      const taggedTag = taggedMatch ? normalize(taggedMatch[1]) : '';
      const taggedColorMatches =
        taggedTag && !MODEL_RE.test(taggedTag) && lower(taggedTag) === lower(this.state.color);

      url.search = '';
      if (view) url.searchParams.set('view', view);
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
      if (this.state.color && !taggedColorMatches) url.searchParams.set('color', this.state.color);
      if (this.state.model) url.searchParams.set('model', this.state.model);
      if (this.state.q) url.searchParams.set('q', this.state.q);

      const next = url.toString();
      if (next === window.location.href) return;
      if (replace) window.history.replaceState({}, '', next);
      else window.history.pushState({}, '', next);
    }
  }

  document.querySelectorAll(SELECTOR).forEach((root) => {
    const app = new DesigncaseIndex(root);
    app.init().catch((error) => {
      console.error(error);
      const loading = root.querySelector('[data-loading]');
      if (loading) loading.textContent = '案例資料載入失敗，請稍後再試。';
    });
  });
})();
